import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FindCandidatesHeader from '../components/FindCandidatesHeader';
import FindCandidatesTable from '../components/shared_components/FindCandidatesTable';
import {
  commitSearchQuery,
  updateActiveSubTab,
  updateActiveTab,
  updateSelectedJob,
  updateSelectedSearch,
} from '../store/slices/findCandidatesSlice';
import {
  getJobSearchesThunk,
  getOutreachCandidatesThunk,
  getSourcingCandidatesThunk,
} from '../store/thunks/FindCandidatesThunk';

/**
 * Find Candidates — the sourced/outreach pipeline, laid out like Interview Management:
 * job dropdown → Sourcing / Outreach tabs → sub-tab pills → one table.
 *
 * Replaces the Job Roles → job detail → per-list drill-down, which sat outside the
 * GenieHire header and made sourcing feel like a separate product.
 *
 * Always mounted under /find-candidates/:jobId — SelectJobRole picks the scope first.
 */
function FindCandidates() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { jobId: routeJobId } = useParams();
  const portal = location.pathname.startsWith('/hr') ? 'hr' : 'org-admin';
  const appliedIncomingJob = useRef(false);

  // A search that came back with less than a full page says so here — what a looser filter
  // would have found. Read on the first render because the effect below clears the state it
  // arrives in, and dismissable since it's news, not a problem to fix.
  const [searchNotice, setSearchNotice] = useState(location.state?.search_notice || null);

  const {
    activeTab,
    activeSubTab,
    activeSubSubTab,
    selectedJobId,
    selectedJobRole,
    selectedSearchId,
    jobRoles,
    currentPage,
    pageSize,
    debouncedSearch,
    searchQuery,
    filters,
  } = useSelector((state) => state.findCandidatesSlice);

  // The URL owns the job scope, so it survives a refresh and can be shared. The role's name
  // isn't in the URL: it comes from whoever navigated here, and is backfilled off the job
  // list once that resolves — otherwise a refresh would leave the header dropdown blank.
  useEffect(() => {
    if (!routeJobId) return;
    const known = (jobRoles || []).find((j) => j.job_id === routeJobId);
    const nameMissing = !selectedJobRole && (known || location.state?.job_role);
    if (routeJobId === selectedJobId && !nameMissing) return;

    dispatch(
      updateSelectedJob({
        jobId: routeJobId,
        jobRole: location.state?.job_role || known?.job_role || '',
      }),
    );
  }, [routeJobId, jobRoles, selectedJobId, selectedJobRole, location.state, dispatch]);

  // Arriving from a just-run search, or from a recent-search row on the search screen: show
  // that job's sourced pool — narrowed to the one search when the caller named it. The state
  // is cleared off the history entry so a later back/forward doesn't silently re-scope the
  // page. This runs after the effect above, so its search scope wins over the reset a job
  // change performs there.
  useEffect(() => {
    if (!location.state?.job_id || appliedIncomingJob.current) return;
    appliedIncomingJob.current = true;

    dispatch(updateActiveTab('SOURCING'));
    dispatch(updateActiveSubTab('ALL'));
    if (location.state.search_id) dispatch(updateSelectedSearch(location.state.search_id));
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, dispatch, navigate]);

  // Options for the "Search :" scope picker. A search belongs to exactly one job role, so
  // this reloads whenever the job does.
  useEffect(() => {
    if (!selectedJobId) return undefined;
    const promise = dispatch(getJobSearchesThunk({ jobId: selectedJobId }));
    return () => promise?.abort?.();
  }, [selectedJobId, dispatch]);

  // Typing shouldn't fire a request per keystroke — the thunks read debouncedSearch only.
  useEffect(() => {
    const timer = setTimeout(() => dispatch(commitSearchQuery(searchQuery.trim())), 500);
    return () => clearTimeout(timer);
  }, [searchQuery, dispatch]);

  const fetchCandidates = useCallback(() => {
    const orgId = localStorage.getItem('current_org_id');
    if (!orgId) return undefined;
    // Every list is job-scoped. On first paint the job roles haven't arrived yet, so hold
    // off rather than firing an org-wide request whose results would be replaced a moment
    // later once the most recent job auto-selects.
    if (!selectedJobId) return undefined;

    const params = {
      filterby: activeSubTab,
      subFilter: activeSubSubTab,
      job_id: selectedJobId,
      search_id: selectedSearchId,
      search: debouncedSearch,
      filters,
      page: currentPage,
      limit: pageSize,
      orgId,
      recruiterId: localStorage.getItem('org_admin_id'),
    };

    return activeTab === 'OUTREACH'
      ? dispatch(getOutreachCandidatesThunk(params))
      : dispatch(getSourcingCandidatesThunk(params));
  }, [
    dispatch,
    activeTab,
    activeSubTab,
    activeSubSubTab,
    selectedJobId,
    selectedSearchId,
    currentPage,
    pageSize,
    debouncedSearch,
    filters,
  ]);

  // One effect owns every refetch, so the table can never disagree with the controls
  // above it. The in-flight request is aborted when the inputs change again — otherwise
  // a slow response for the previous tab can land after the new one and overwrite it.
  useEffect(() => {
    const promise = fetchCandidates();
    return () => promise?.abort?.();
  }, [fetchCandidates]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
      <div className="relative min-h-screen w-full rounded-2xl border-gray-700/50 dark:glass-effect dark:text-white bg-bgSecondary text-textPrimary px-8 transition-colors flex flex-col">
        <FindCandidatesHeader portal={portal} onRefetch={fetchCandidates} />
        {searchNotice && (
          <div className="mt-3 flex items-start gap-3 px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <span className="flex-1">{searchNotice}</span>
            <button
              type="button"
              onClick={() => setSearchNotice(null)}
              className="text-amber-500 hover:text-amber-700 leading-none text-base"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        <div className="w-full min-h-80 flex flex-row">
          <FindCandidatesTable portal={portal} onRefetch={fetchCandidates} />
        </div>
      </div>
  );
}

export default FindCandidates;
